import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import jaduVsADUImage from '@/assets/jadu-vs-adu.jpg';

const WhatsDifferenceBetweenJADUAndADU = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/jadu-vs-adu-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={jaduVsADUImage} 
              alt="JADU vs ADU floor plan comparison" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              What's the Difference Between a JADU and an ADU?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Understand clearly the differences between Junior ADUs (JADU) and regular ADUs in California. Learn size, kitchen requirements, and permitting distinctions.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Defining JADU vs ADU in California
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Junior ADUs (JADUs) are smaller accessory units limited to 500 square feet, while standard ADUs can reach 1,200 square feet. JADUs are typically created within or attached to existing primary residences, while ADUs can be detached, attached, or converted structures. Both provide independent living space but serve different housing needs and budgets.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Key Differences in Size, Kitchens, and Entrances
              </h2>
              <p className="text-white leading-relaxed mb-6">
                JADUs require only efficiency kitchens (microwave, hot plate, small refrigerator) while ADUs need full kitchens with standard appliances. JADUs must have separate exterior entrances but can share utilities with the main house. ADUs require independent utility systems and can have multiple bedrooms, while JADUs are typically studio or one-bedroom configurations.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Permitting and Legal Requirements for JADUs and ADUs
              </h2>
              <p className="text-white leading-relaxed mb-6">
                JADU permitting is often simpler and less expensive than ADU permitting, with reduced impact fees and streamlined approval processes. JADUs typically require owner-occupancy of either the main house or JADU unit, while ADUs have no such restriction. Both require building permits but JADUs face fewer infrastructure upgrade requirements.
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: What's the maximum size for a JADU compared to an ADU?</p>
                  <p>A: JADUs are limited to 500 sq. ft., while ADUs can be up to 1,200 sq. ft. depending on city rules.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Can a JADU have a separate entrance?</p>
                  <p>A: Yes, JADUs typically must have a separate exterior entry.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default WhatsDifferenceBetweenJADUAndADU;